import { t } from '../i18n/index.js?v=20260727-atlas-guide-3';
import {
  adminCreateGameNotice,
  adminModerateUser,
  fetchMyGameNotices,
  listAdminAuditLog,
  listAdminChatHistory,
  markGameNoticeRead,
  searchAdminUsers,
} from './admin-api.js';

const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));
const formatDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};
const durationOptions = [
  ['10', '10 min'],
  ['60', '1 hora'],
  ['1440', '24 horas'],
  ['10080', '7 dias'],
  ['0', 'Indefinido'],
];

let hooks = { context: { is_admin: false }, showToast: () => {} };
let root = null;
let users = [];
let selectedUser = null;
let selectedAudit = [];
let auditRequestToken = 0;
let currentView = 'users';
let busy = false;

function ensureRoot() {
  if (root) return root;
  root = document.createElement('div');
  root.id = 'adminDialog';
  root.className = 'city-dialog hidden admin-dialog';
  root.innerHTML = `
    <div class="city-dialog-card admin-dialog-card" role="dialog" aria-modal="true" aria-labelledby="adminDialogTitle">
      <button type="button" class="dialog-close" data-admin-close aria-label="Fechar">&times;</button>
      <span class="eyebrow">ADMIN</span>
      <h2 id="adminDialogTitle">Admin</h2>
      <p class="admin-subtitle">Moderação, histórico e avisos do jogo.</p>
      <div class="admin-tabs" role="tablist">
        <button type="button" class="admin-tab active" data-admin-view="users" role="tab">Jogadores</button>
        <button type="button" class="admin-tab" data-admin-view="history" role="tab">Histórico do chat</button>
      </div>
      <div class="admin-view" data-admin-view-panel="users">
        <form class="admin-search" data-admin-search-form>
          <input data-admin-search-input type="search" maxlength="64" placeholder="Buscar treinador..." autocomplete="off">
          <button type="submit" class="admin-btn primary">Buscar</button>
        </form>
        <div class="admin-users" data-admin-users></div>
        <section class="admin-selected" data-admin-selected hidden></section>
      </div>
      <div class="admin-view hidden" data-admin-view-panel="history">
        <form class="admin-search" data-admin-history-form>
          <select data-admin-history-channel aria-label="Canal">
            <option value="">Todos os canais</option><option value="world">Mundo</option><option value="trade">Troca</option><option value="help">Ajuda</option>
          </select>
          <input data-admin-history-query type="search" maxlength="64" placeholder="Nome ou texto..." autocomplete="off">
          <button type="submit" class="admin-btn primary">Carregar</button>
        </form>
        <div class="admin-history" data-admin-history></div>
      </div>
    </div>`;
  document.body.append(root);
  root.addEventListener('click', onClick);
  root.querySelector('[data-admin-search-form]')?.addEventListener('submit', event => {
    event.preventDefault();
    void searchUsers(root.querySelector('[data-admin-search-input]')?.value || '');
  });
  root.querySelector('[data-admin-history-form]')?.addEventListener('submit', event => {
    event.preventDefault();
    void loadHistory();
  });
  return root;
}

function onClick(event) {
  const close = event.target.closest('[data-admin-close]');
  if (close || event.target === root) {
    closeAdminPanel();
    return;
  }
  const view = event.target.closest('[data-admin-view]');
  if (view) {
    currentView = view.dataset.adminView === 'history' ? 'history' : 'users';
    renderView();
    if (currentView === 'history') void loadHistory();
    return;
  }
  const select = event.target.closest('[data-admin-select-user]');
  if (select) {
    selectedUser = users.find(user => user.user_id === select.dataset.adminSelectUser) || null;
    selectedAudit = [];
    auditRequestToken += 1;
    renderUsers();
    void loadAudit();
    return;
  }
  const action = event.target.closest('[data-admin-apply-action]');
  if (action) {
    void applyModeration();
    return;
  }
  const notice = event.target.closest('[data-admin-send-notice]');
  if (notice) {
    void sendNotice();
    return;
  }
  const historyUser = event.target.closest('[data-admin-history-user]');
  if (historyUser) {
    const user = users.find(item => item.user_id === historyUser.dataset.adminHistoryUser);
    if (user) {
      selectedUser = user;
      selectedAudit = [];
      auditRequestToken += 1;
      currentView = 'users';
      renderView();
      renderUsers();
      void loadAudit();
    } else {
      void searchUsers(historyUser.dataset.adminHistoryName || '').then(() => {
        const found = users.find(item => item.user_id === historyUser.dataset.adminHistoryUser);
        if (!found) return;
        selectedUser = found;
        selectedAudit = [];
        auditRequestToken += 1;
        currentView = 'users';
        renderView();
        renderUsers();
        void loadAudit();
      });
    }
  }
}

function renderView() {
  ensureRoot();
  root.querySelectorAll('[data-admin-view]').forEach(button => {
    const active = button.dataset.adminView === currentView;
    button.classList.toggle('active', active);
    button.setAttribute('aria-selected', String(active));
  });
  root.querySelectorAll('[data-admin-view-panel]').forEach(panel => {
    panel.classList.toggle('hidden', panel.dataset.adminViewPanel !== currentView);
  });
}

function statusLabel(user) {
  const flags = [];
  const activeUntil = value => value === 'infinity' || (value && Date.parse(value) > Date.now());
  if (user.banned_at) flags.push('BANIDO');
  if (activeUntil(user.muted_until)) flags.push('MUTADO');
  if (activeUntil(user.silenced_until)) flags.push('SILENCIADO');
  if (user.preserve_messages) flags.push('HISTÓRICO PRESERVADO');
  return flags.join(' · ') || 'Ativo';
}

function renderUsers() {
  const list = root?.querySelector('[data-admin-users]');
  if (!list) return;
  list.innerHTML = users.length ? users.map(user => `
    <button type="button" class="admin-user-row ${selectedUser?.user_id === user.user_id ? 'selected' : ''}" data-admin-select-user="${esc(user.user_id)}">
      <span><b>${esc(user.trainer_name)}</b><small>${esc(statusLabel(user))}</small></span>
      <em>Selecionar</em>
    </button>`).join('') : '<p class="admin-empty">Nenhum treinador encontrado.</p>';
  renderSelected();
}

function renderSelected(audit = selectedAudit) {
  const panel = root?.querySelector('[data-admin-selected]');
  if (!panel) return;
  selectedAudit = Array.isArray(audit) ? audit : [];
  if (!selectedUser) {
    panel.hidden = true;
    panel.innerHTML = '';
    return;
  }
  panel.hidden = false;
  panel.innerHTML = `
    <header class="admin-selected-head"><div><span class="eyebrow">JOGADOR SELECIONADO</span><h3>${esc(selectedUser.trainer_name)}</h3></div><span class="admin-status">${esc(statusLabel(selectedUser))}</span></header>
    <div class="admin-action-grid">
      <label>Ação<select data-admin-action>
        <option value="ban">Banir conta</option><option value="unban">Desbanir conta</option>
        <option value="mute">Mutar chat</option><option value="unmute">Remover mute</option>
        <option value="silence">Silenciar (shadow)</option><option value="unsilence">Remover silêncio</option>
      </select></label>
      <label>Duração<select data-admin-duration>${durationOptions.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')}</select></label>
    </div>
    <label class="admin-field">Motivo<textarea data-admin-reason maxlength="500" rows="2" placeholder="Descreva o motivo da ação..."></textarea></label>
    <button type="button" class="admin-btn danger" data-admin-apply-action ${busy ? 'disabled' : ''}>Aplicar moderação</button>
    <div class="admin-notice-box"><b>Aviso no jogo</b><textarea data-admin-notice-body maxlength="500" rows="2" placeholder="Mensagem que aparecerá para este jogador..."></textarea><button type="button" class="admin-btn" data-admin-send-notice ${busy ? 'disabled' : ''}>Enviar aviso</button></div>
    <details class="admin-audit"><summary>Auditoria recente</summary>${audit.length ? audit.map(entry => `<p><b>${esc(entry.action)}</b> · ${esc(formatDate(entry.created_at))}<br><small>${esc(entry.reason || '—')}</small></p>`).join('') : '<p>Nenhuma ação registrada.</p>'}</details>`;
}

async function searchUsers(query) {
  if (!hooks.context?.is_admin || busy) return;
  busy = true;
  try {
    const previousUserId = selectedUser?.user_id || null;
    users = await searchAdminUsers(query, 25);
    selectedUser = users.find(user => user.user_id === selectedUser?.user_id) || users[0] || null;
    if (selectedUser?.user_id !== previousUserId) {
      selectedAudit = [];
      auditRequestToken += 1;
    }
    renderUsers();
    if (selectedUser) await loadAudit();
  } catch (error) {
    console.error('admin user search failed', error);
    hooks.showToast('Não foi possível buscar jogadores.');
  } finally {
    busy = false;
    renderSelected();
  }
}

async function loadAudit() {
  const userId = selectedUser?.user_id;
  if (!userId || !hooks.context?.is_admin) return;
  const requestToken = ++auditRequestToken;
  try {
    const audit = await listAdminAuditLog(userId, 20);
    if (requestToken !== auditRequestToken || selectedUser?.user_id !== userId) return;
    renderSelected(audit);
  } catch (error) {
    console.error('admin audit load failed', error);
  }
}

async function applyModeration() {
  if (!selectedUser || busy) return;
  const action = root.querySelector('[data-admin-action]')?.value || 'mute';
  const durationMinutes = Number(root.querySelector('[data-admin-duration]')?.value || 60);
  const reason = String(root.querySelector('[data-admin-reason]')?.value || '').trim();
  if (!['unban', 'unmute', 'unsilence'].includes(action) && reason.length < 3) {
    hooks.showToast('Informe um motivo com pelo menos 3 caracteres.');
    return;
  }
  busy = true;
  try {
    await adminModerateUser(selectedUser.user_id, action, durationMinutes, reason);
    hooks.showToast(`Ação aplicada em ${selectedUser.trainer_name}.`);
    busy = false;
    await searchUsers(selectedUser.trainer_name);
  } catch (error) {
    console.error('admin moderation failed', error);
    hooks.showToast('Não foi possível aplicar a moderação.');
  } finally {
    busy = false;
    renderSelected();
  }
}

async function sendNotice() {
  if (!selectedUser || busy) return;
  const body = String(root.querySelector('[data-admin-notice-body]')?.value || '').trim();
  if (!body) {
    hooks.showToast('Escreva o aviso antes de enviar.');
    return;
  }
  busy = true;
  try {
    await adminCreateGameNotice(selectedUser.user_id, body, 'warning', 48);
    hooks.showToast(`Aviso enviado para ${selectedUser.trainer_name}.`);
    const field = root.querySelector('[data-admin-notice-body]');
    if (field) field.value = '';
    busy = false;
    await loadAudit();
  } catch (error) {
    console.error('admin notice failed', error);
    hooks.showToast('Não foi possível enviar o aviso.');
  } finally {
    busy = false;
    renderSelected();
  }
}

async function loadHistory() {
  const list = root?.querySelector('[data-admin-history]');
  if (!list || !hooks.context?.is_admin) return;
  list.innerHTML = '<p class="admin-empty">Carregando histórico...</p>';
  try {
    const channel = root.querySelector('[data-admin-history-channel]')?.value || null;
    const query = root.querySelector('[data-admin-history-query]')?.value || '';
    const messages = await listAdminChatHistory(channel, query, 200);
    list.innerHTML = messages.length ? messages.map(message => `
      <article class="admin-history-row">
        <header><b>${esc(message.sender_name)}</b><span>${esc(message.channel)} · ${esc(formatDate(message.created_at))}${message.preserve_forever ? ' · PRESERVADA' : ''}</span></header>
        <p>${esc(message.body || '[anexo]')}</p>
        <button type="button" class="admin-link" data-admin-history-user="${esc(message.sender_id)}" data-admin-history-name="${esc(message.sender_name)}">Abrir jogador</button>
      </article>`).join('') : '<p class="admin-empty">Nenhuma mensagem no período de retenção.</p>';
  } catch (error) {
    console.error('admin history load failed', error);
    list.innerHTML = '<p class="admin-empty">Não foi possível carregar o histórico.</p>';
  }
}

export function initAdminUI(options = {}) {
  hooks = { ...hooks, ...options };
  ensureRoot();
  renderView();
}

export function openAdminPanel({ user = null } = {}) {
  if (!hooks.context?.is_admin) return false;
  ensureRoot();
  selectedUser = user;
  selectedAudit = [];
  auditRequestToken += 1;
  root.classList.remove('hidden');
  renderView();
  renderUsers();
  if (selectedUser) void loadAudit();
  else void searchUsers('');
  return true;
}

export function closeAdminPanel() {
  root?.classList.add('hidden');
}

export async function initGameNoticeFeed({ userId, showToast = () => {} } = {}) {
  if (!userId) return () => {};
  let stopped = false;
  const poll = async () => {
    if (stopped || document.visibilityState === 'hidden') return;
    try {
      const notices = await fetchMyGameNotices(20);
      if (!notices.length) return;
      showToast(notices.map(notice => `${notice.kind === 'warning' ? '⚠ Aviso da equipe' : 'Aviso'}: ${notice.body}`).join('\n'));
      await Promise.all(notices.map(notice => markGameNoticeRead(notice.id)));
    } catch (error) {
      console.warn('game notices load failed', error);
    }
  };
  await poll();
  const timer = setInterval(poll, 30000);
  return () => { stopped = true; clearInterval(timer); };
}

export function openAdminForUser(user) {
  return openAdminPanel({ user });
}
